import { Injectable, computed, inject, signal } from '@angular/core'
import { ADMIN_ROLES, AdminPermission, AdminRoleKey } from '../models/admin-role.model'
import { AdminApiService } from './admin-api.service'

/**
 * Real admin role, read from `GET /admin/me` (the Authentik JWT's group
 * membership resolved server-side — see `admin/domain/staffRole.ts` on the
 * backend). Replaces the old FE-only `localStorage` mock: there is no
 * client-side role switcher any more, and the default while `/me` is
 * loading is `read_only` (the previous mock defaulted to `super_admin`,
 * which was an elevation-by-default bug — never repeat that here).
 */
@Injectable({ providedIn: 'root' })
export class AdminRoleService {
  private api = inject(AdminApiService)
  private roleDefs = ADMIN_ROLES

  currentRole = signal<AdminRoleKey>('read_only')
  loaded = signal(false)

  currentPermissions = computed<AdminPermission[]>(() => this.roleDefs.find(r => r.key === this.currentRole())?.permissions ?? [])

  roles = this.roleDefs

  /** Called once at app init (see app.config.ts / a root resolver). */
  load(): void {
    this.api.getMe().subscribe({
      next: res => {
        this.currentRole.set((res.data.role as AdminRoleKey) || 'read_only')
        this.loaded.set(true)
      },
      error: () => {
        // Fail closed: no role claim resolved, stay at read_only.
        this.loaded.set(true)
      },
    })
  }

  hasPermission(permission: AdminPermission): boolean {
    const perms = this.currentPermissions()
    return perms.includes('*') || perms.includes(permission)
  }

  roleLabel(role: AdminRoleKey): string {
    return this.roleDefs.find(r => r.key === role)?.label ?? role
  }
}
